import {
  MouseEvent as ReactMouseEvent,
  FocusEvent as ReactFocusEvent,
} from 'react';

/**
 * Shared interaction behavior between Number and Text spin buttons.
 * @internal
 */
export function useSpinButtonInteraction(props: {
  disabled: boolean;
  selectAll: () => void;
  deselectAll: () => void;
  rootElement: HTMLSpanElement | null;
}): {
  onClick: (event: ReactMouseEvent) => void;
  onFocus: (event: ReactFocusEvent) => void;
  onBlur: (event: ReactFocusEvent) => void;
} {
  const { disabled, selectAll, deselectAll, rootElement } = props;
  return {
    onClick: (event: ReactMouseEvent) => {
      if (disabled) {
        event.preventDefault();
      } else {
        selectAll();
      }
    },
    onFocus: (event: ReactFocusEvent) => {
      if (
        event.target.hasAttribute('contenteditable') === false ||
        event.target.getAttribute('contenteditable') === 'false'
      ) {
        return;
      }

      // If the current selection spans multiple nodes (e.g., from a drag-select
      // operation across multiple spin buttons), don't overwrite it with selectAll().
      // This preserves drag-select across multiple spin buttons.
      const selection = window.getSelection();
      const selectionSpansMultipleNodes =
        selection &&
        selection.anchorNode !== selection.focusNode &&
        selection.toString().length > 0;

      // Check if we're in the middle of restoring contenteditable after a
      // drag-select. The browser fires focus synchronously during restoration,
      // before the selection can be restored via setBaseAndExtent.
      const isRestoringSelection =
        (event.target as HTMLElement)
          .closest('.strato-inputgroup')
          ?.hasAttribute('data-restoring-selection') ?? false;

      if (selectionSpansMultipleNodes || isRestoringSelection) {
        return;
      }

      if (disabled) {
        event.preventDefault();
      } else {
        selectAll();
        rootElement?.scrollIntoView({
          block: 'nearest',
          inline: 'nearest',
        });
      }
    },
    onBlur: (event: ReactFocusEvent) => {
      if (
        event.target.hasAttribute('contenteditable') === false ||
        event.target.getAttribute('contenteditable') === 'false'
      ) {
        return;
      }

      deselectAll();
    },
  };
}
